<template>
  <WizardModal
    :show-modal="isVisible"
    :modal-title="t('Conciliação de Recebimentos')"
    :steps="wizardStepsConfig"
    size="xlarge"
    :show-summary="currentStepIndex >= 2"
    :summary-items="summaryItems"
    :reset-on-close="true"
    @close="onClose"
    @cancel="onClose"
    @step-change="(handleStepChange as any)"
    @finish="completeRevenueConciliation">

    <!-- Step 0: Payment Status Review (NEW) -->
    <template #step-0>
      <div class="step-panel">
        <h3>{{ t('Verificação de Status de Pagamento') }}</h3>
        <p class="step-description">
          {{ t('Verifique os compromissos do período e crie recebimentos para os que estão pendentes ou parciais antes de prosseguir.') }}
        </p>

        <!-- Dashboard KPIs (compact mode for wizard) -->
        <PaymentStatusKPIs
          :metrics="paymentMetrics ?? undefined"
          :loading="isLoadingPaymentStatus"
          :show-total-summary="false"
          :compact="true" />

        <!-- Payment Status Table with actions -->
        <PaymentStatusTable
          ref="paymentStatusTableRef"
          :compromissos="compromissosWithPaymentStatus"
          :is-creating="isBulkCreating"
          :show-csv-import="true"
          :show-header-actions="true"
          :show-refresh-button="true"
          :compact="true"
          :page-size="100"
          :hide-conciliated="true"
          :has-subaccount="franchiseHasSubaccount"
          data-testid="payroll-revenue-payment-status-table"
          @refresh="loadPaymentStatusData"
          @import-csv="openCsvImportModal"
          @create-recebimentos="(handleBulkCreateRecebimentos as any)"
          @view-compromisso="openCompromissoModal"
          @view-cliente="openClienteModal"
          @open-recebimento-wizard="openRecebimentoWizard" />
      </div>

      <!-- RecebimentosCsvImportModal is centralized in ModalManager.vue - opened via eventBus -->
    </template>

    <!-- Step 1: Select Available Recebimentos (was Step 0) -->
    <template #step-1>
      <div class="step-panel">
        <h3>{{ t('Selecione os recebimentos para vincular') }}</h3>
        <p class="step-description">
          {{ t('Selecione os recebimentos para vincular a este período de folha') }}
        </p>

        <!-- Loading State -->
        <div v-if="isLoadingRecebimentos" class="loading-state" data-testid="payroll-revenue-loading-recebimentos">
          <i class="pi pi-spin pi-spinner"></i>
          <p>{{ t('Carregando recebimentos...') }}</p>
        </div>

        <!-- Selection Summary -->
        <div v-else-if="displayRecebimentos.length > 0" class="selection-summary">
          <div class="summary-item">
            <span class="summary-label">{{ t('Disponíveis') }}:</span>
            <span class="summary-value" data-testid="payroll-revenue-available-count">{{ displayRecebimentos.length }}</span>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-item">
            <span class="summary-label">{{ t('Selecionados') }}:</span>
            <span class="summary-value" data-testid="payroll-revenue-selected-count">{{ selectedRecebimentos.length }}</span>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-item">
            <span class="summary-label">{{ t('Total Selecionados') }}:</span>
            <span class="summary-value" data-testid="payroll-revenue-selected-total">{{ n(selectedTotal, 'currency') }}</span>
          </div>
        </div>

        <!-- Recebimentos DataTable with Selection -->
        <DataTable
          v-if="!isLoadingRecebimentos && displayRecebimentos.length > 0"
          v-model:selection="selectedRecebimentos"
          :config="recebimentosTableConfig"
          :data="displayRecebimentos"
          class="recebimentos-datatable"
          data-testid="payroll-revenue-recebimentos-table"
          @edit-click="openRecebimentoModal"
        />

        <!-- Empty State -->
        <div v-else-if="!isLoadingRecebimentos" class="empty-state" data-testid="payroll-revenue-empty-state">
          <i class="pi pi-inbox"></i>
          <p>{{ t('Nenhum recebimento disponível para o período selecionado') }}</p>
          <p class="empty-state-hint">{{ t('Verifique o passo anterior para criar recebimentos pendentes.') }}</p>
        </div>
      </div>
    </template>

    <!-- Step 2: Complete Conciliation (was Step 1) -->
    <template #step-2>
      <div class="step-panel">
        <h3>{{ t('Finalizar Conciliação de Recebimentos') }}</h3>
        <p class="step-description">
          {{ t('Revise o resumo final e complete o processo de conciliação de recebimentos.') }}
        </p>

        <!-- Final Summary -->
        <div class="final-summary">
          <div class="summary-section">
            <h4>{{ t('Resumo da Conciliação') }}</h4>
            <div class="summary-grid">
              <div class="summary-row">
                <span class="label">{{ t('Total de Recebimentos Selecionados') }}:</span>
                <span class="value" data-testid="payroll-revenue-summary-count">{{ selectedRecebimentos.length }}</span>
              </div>
              <div class="summary-row highlight">
                <span class="label">{{ t('Total de Valor Selecionado') }}:</span>
                <span class="value" data-testid="payroll-revenue-summary-total">{{ n(selectedTotal, 'currency') }}</span>
              </div>
            </div>
          </div>

          <div class="completion-message" data-testid="payroll-revenue-completion-message">
            <i class="pi pi-check-circle"></i>
            <p>{{ t('Clique em "Finalizar Conciliação" para vincular os recebimentos selecionados e finalizar o processo.') }}</p>
            <p class="note">{{ t('Após a conclusão, o valor conciliado será usado no cálculo de folha.') }}</p>
          </div>
        </div>

      </div>
    </template>
  </WizardModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConfirmAction } from '../../../composables/useConfirmAction';
import WizardModal from '../../../ui/WizardModal.vue';
import PaymentStatusKPIs from '../../../components/payment-status/PaymentStatusKPIs.vue';
import PaymentStatusTable from '../../../components/payment-status/PaymentStatusTable.vue';
// RecebimentosCsvImportModal is centralized in ModalManager.vue - opened via eventBus
import { useRevenueConciliation } from '../../../composables/useRevenueConciliation';
import { useRecebimento } from '../../../composables/useRecebimento';
import { useFranchisesSubaccounts } from '../../../composables/useFranchisesSubaccounts';
import { eventBus } from '../../../eventBus';
import { log } from '../../../utils/DebugLogger';
import { config } from '../../../services/configService';
// Feature `baas` (backend registry, skill feature-flags).
const baasEnabled = config.isFeatureEnabled('baas');


type Id = number | string;

interface Recebimento {
  recebimento_id: Id;
  recebimento_data?: string | null;
  recebimento_data_cadastro?: string | null;
  compromisso_data?: string | null;
  recebimento_valor_display?: number | string;
  recebimento_origem?: string;
  recebimento_origem_id?: Id | null;
  forma_pagamento_nome?: string;
  operador_nome?: string;
  [key: string]: unknown;
}

interface CompromissoWithPaymentStatus {
  compromisso_id: Id;
  compromisso_status_financeiro?: number | string;
  valor_restante?: number;
  [key: string]: unknown;
}

interface PaymentMetrics {
  total: number;
  totalValor: number;
  complete: number;
  completeValor: number;
  partial: number;
  partialValor: number;
  due: number;
  dueValor: number;
}

interface RevenueConciliationSummary {
  status: string;
  amount: number;
}

interface BulkRecebimentosResults {
  success: unknown[];
  errors: unknown[];
}

interface PaymentStatusTableInstance {
  clearSelection: () => void;
}

interface MotivoInfo {
  type: 'recebido' | 'cadastrado' | 'agendado' | 'outro';
  label: string;
  tooltip: string;
  severity: 'success' | 'info' | 'warn' | 'secondary';
}

interface Props {
  visible: boolean;
  periodId: number;
  franchiseId: number;
  startDate: string | Date;
  endDate: string | Date;
  userIds?: Array<Id>;
  /**
   * Payment Rule IDs for the HYBRID ARCHITECTURE
   *
   * ╔════════════════════════════════════════════════════════════════════════════╗
   * ║  WHY paymentRuleIds (not executionIds)?                                    ║
   * ║  → The business constraint is at the RULE level, not execution level       ║
   * ║  → Junction table has UNIQUE(payment_rule_id, recebimento_id)              ║
   * ║  → Different executions of the SAME rule cannot claim the same recebimento ║
   * ║                                                                            ║
   * ║  EXAMPLE:                                                                  ║
   * ║  - Payroll has 2 rules: Kevin (rule_id=1) + João (rule_id=2)               ║
   * ║  - API call: paymentRuleIds: [1, 2]                                        ║
   * ║  - Returns: recebimentos NOT yet claimed by rule 1 OR rule 2               ║
   * ╚════════════════════════════════════════════════════════════════════════════╝
   */
  paymentRuleIds?: Array<Id>;
}

const props = withDefaults(defineProps<Props>(), {
  userIds: () => [],
  paymentRuleIds: () => [],
});

const emit = defineEmits<{
  'update:visible': [value: boolean];
  'completed': [result: unknown];
  'closed': [];
}>();

// Composables
const { t, n } = useI18n();
const { confirmAction } = useConfirmAction();
const {
  getRevenueConciliationSummary,
  linkRecebimentosToPeriod,
  unlinkRecebimentoFromPeriod,
  completeRevenueConciliation: completeRevenueConciliationAPI,
  getLinkedRecebimentos: getLinkedRecebimentosAPI
} = useRevenueConciliation();

const {
  fetchAvailableRecebimentosForPaymentRules,
  fetchCompromissosWithPaymentStatus,
  calculateDashboardMetrics,
  createBulkRecebimentos
} = useRecebimento();

const {
  getByFranchise: getSubaccountByFranchise
} = useFranchisesSubaccounts();

// State
const isVisible = ref<boolean>(props.visible);
const currentStepIndex = ref<number>(0);
const availableRecebimentos = ref<Recebimento[]>([]);
const selectedRecebimentos = ref<Recebimento[]>([]);
const originallyLinkedRecebimentos = ref<Recebimento[]>([]);
const conciliationStatus = ref<string>('pending');
const conciliationAmount = ref<number>(0);
const isCompleting = ref<boolean>(false);

// Step 0: Payment Status state
const compromissosWithPaymentStatus = ref<CompromissoWithPaymentStatus[]>([]);
const paymentMetrics = ref<PaymentMetrics | null>(null);
const isLoadingPaymentStatus = ref<boolean>(false);
const isBulkCreating = ref<boolean>(false);
const paymentStatusTableRef = ref<PaymentStatusTableInstance | null>(null);
const franchiseHasSubaccount = ref<boolean>(false);

// Step 1: Loading state for recebimentos
const isLoadingRecebimentos = ref<boolean>(false);

// Wizard steps configuration for WizardModal (3 steps now)
const wizardStepsConfig = [
  {
    title: 'Verificar Pagamentos',
    description: 'Verifique o status de pagamento dos compromissos',
    icon: 'pi-search-dollar',
    validate: () => true, // Optional step - user can proceed without changes
    optional: true
  },
  {
    title: 'Selecionar Recebimentos',
    description: 'Selecione os recebimentos para vincular a este período de folha',
    icon: 'pi-list',
    validate: () => selectedRecebimentos.value.length > 0
  },
  {
    title: 'Finalizar Conciliação',
    description: 'Finalize a conciliação de recebimentos',
    icon: 'pi-check-circle',
    validate: () => selectedRecebimentos.value.length > 0
  }
];

// Helper to format date for API
const formatDateForApi = (date: string | Date | null | undefined): string | null => {
  if (!date) return null;
  // Parse the date (handles both Date objects and ISO 8601 strings like "2025-10-08T03:00:00Z")
  const d = typeof date === 'string' ? new Date(date) : new Date(date);
  // Return date-only format (YYYY-MM-DD) for query override headers
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Helper to check if a date is within the period
const isDateInPeriod = (dateStr: string | null | undefined): boolean => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const start = new Date(formatDateForApi(props.startDate) ?? '');
  const end = new Date(formatDateForApi(props.endDate) ?? '');
  // Set time to start/end of day for accurate comparison
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  date.setHours(12, 0, 0, 0);
  return date >= start && date <= end;
};

/**
 * Get the "motivo" (reason) for why a recebimento is in the list
 * Returns: { type: 'recebido' | 'cadastrado' | 'agendado', label: string, tooltip: string, severity: string }
 */
const getMotivoInfo = (recebimento: Recebimento): MotivoInfo => {
  const inRecebimentoData = isDateInPeriod(recebimento.recebimento_data);
  const inCadastroData = isDateInPeriod(recebimento.recebimento_data_cadastro);
  const inCompromissoData = isDateInPeriod(recebimento.compromisso_data);

  // Priority: Recebido > Cadastrado > Agendado
  if (inRecebimentoData) {
    return {
      type: 'recebido',
      label: t('Recebido'),
      tooltip: t('Valor recebido dentro do período selecionado'),
      severity: 'success' // green
    };
  }
  if (inCadastroData) {
    return {
      type: 'cadastrado',
      label: t('Cadastrado'),
      tooltip: t('Recebimento registrado no sistema dentro do período'),
      severity: 'info' // blue
    };
  }
  if (inCompromissoData) {
    return {
      type: 'agendado',
      label: t('Agendado'),
      tooltip: t('Compromisso agendado dentro do período'),
      severity: 'warn' // orange
    };
  }

  // Fallback (should not happen in normal use)
  return {
    type: 'outro',
    label: '-',
    tooltip: '',
    severity: 'secondary'
  };
};

/**
 * Badge config function for the Motivo column - returns { label, severity, tooltip }
 * Used with DataTable's renderType: 'badge'
 */
const motivoBadgeConfig = (data: Recebimento): MotivoInfo => {
  return getMotivoInfo(data);
};

// Helper to get recebimento value
const getRecebimentoValor = (rec: Recebimento): number => {
  return parseFloat(String(rec.recebimento_valor_display ?? 0)) || 0;
};

// Computed
const selectedTotal = computed<number>(() => {
  const total = selectedRecebimentos.value.reduce((sum, rec) => {
    return sum + getRecebimentoValor(rec);
  }, 0);

  log('RevenueConciliationWizard', 'selectedTotal', 'Calculating total', {
    selectedCount: selectedRecebimentos.value.length,
    total,
    firstRecebimento: selectedRecebimentos.value[0]
  });

  return total;
});

// Merge available recebimentos with originally linked ones for display
const displayRecebimentos = computed<Recebimento[]>(() => {
  const combined: Recebimento[] = [...availableRecebimentos.value];

  for (const linked of originallyLinkedRecebimentos.value) {
    if (!combined.find((r) => r.recebimento_id === linked.recebimento_id)) {
      combined.push(linked);
    }
  }

  return combined;
});

// Summary items for WizardModal
const summaryItems = computed(() => {
  return [
    {
      label: t('Total Selecionados'),
      value: selectedRecebimentos.value.length,
      icon: 'pi pi-check-square'
    },
    {
      label: t('Valor Selecionado'),
      value: n(selectedTotal.value, 'currency'),
      icon: 'pi pi-money-bill'
    }
  ];
});

// DataTable configuration for recebimentos (Step 1)
const recebimentosTableConfig = computed(() => ({
  columns: [
    {
      data: 'recebimento_id',
      title: t('ID'),
      width: 70,
      sortable: false,
      renderType: 'clickable_id'
    },
    {
      data: null,
      title: t('Motivo'),
      width: 100,
      sortable: false,
      filterable: false,
      renderType: 'badge',
      badgeConfig: motivoBadgeConfig
    },
    {
      data: 'recebimento_data',
      title: t('Data recebimento'),
      renderType: 'date',
      width: 110
    },
    {
      data: 'recebimento_data_cadastro',
      title: t('Data cadastro'),
      renderType: 'date',
      width: 110
    },
    {
      data: 'compromisso_data',
      title: t('Data compromisso'),
      renderType: 'date',
      width: 120
    },
    {
      data: 'forma_pagamento_nome',
      title: t('Forma de Pagamento')
    },
    {
      data: 'operador_nome',
      title: t('Operador')
    },
    {
      data: 'recebimento_origem',
      title: t('Origem')
    },
    {
      data: 'recebimento_origem_id',
      title: t('ID Origem'),
      width: 80,
      sortable: false,
      renderType: 'custom_clickable_id',
      clickCallback: openCompromissoModalFromRecebimento
    },
    {
      data: 'recebimento_valor_display',
      title: t('Valor'),
      renderType: 'currency',
      width: 120
    }
  ],
  checkbox: {
    enabled: true,
    position: 'first',
    width: '3rem'
  },
  pagination: false,
  showFilters: false,
  sortable: true,
  stripedRows: true,
  showGridlines: false,
  size: 'small',
  height: '350px',
  heightMode: 'fixed'
}));

// Open RecebimentoModal when clicking on recebimento ID
const openRecebimentoModal = (recebimentoId: Id): void => {
  log('RevenueConciliationWizard', 'openRecebimentoModal', 'Opening recebimento modal', { recebimentoId });
  eventBus.emit('recebimento.open-modal-edit', recebimentoId);
};

// Open GestaoCompromissoModal when clicking on recebimento_origem_id
function openCompromissoModalFromRecebimento(origemId: Id | null | undefined, _rowData?: Recebimento): void {
  if (!origemId) {
    log('RevenueConciliationWizard', 'openCompromissoModalFromRecebimento', 'No origem ID provided');
    return;
  }
  log('RevenueConciliationWizard', 'openCompromissoModalFromRecebimento', 'Opening compromisso modal', { origemId });
  eventBus.emit('gestao-compromisso.open', { compromissoId: Number(origemId) });
}

// Step 0 handlers
const openCompromissoModal = (compromissoId: Id): void => {
  log('RevenueConciliationWizard', 'openCompromissoModal', 'Opening compromisso modal', compromissoId);
  eventBus.emit('compromisso.editRequested', compromissoId);
};

const openClienteModal = (clienteId: Id): void => {
  log('RevenueConciliationWizard', 'openClienteModal', 'Opening cliente modal', clienteId);
  eventBus.emit('cliente.edit', clienteId);
};

const openRecebimentoWizard = ({ compromissoId, franquiaId }: { compromissoId: Id; franquiaId: Id }): void => {
  log('RevenueConciliationWizard', 'openRecebimentoWizard', 'Opening recebimento wizard', { compromissoId, franquiaId });
  eventBus.emit('recebimento-wizard.open', { compromissoId, franquiaId });
};

const openCsvImportModal = (): void => {
  log('RevenueConciliationWizard', 'openCsvImportModal', 'Opening CSV import modal via eventBus');
  eventBus.emit('modal.recebimentos-csv-import.open', {});
};

const handleCsvImported = (): void => {
  log('RevenueConciliationWizard', 'handleCsvImported', 'CSV imported, reloading data');
  loadPaymentStatusData();
};

const handleBulkCreateRecebimentos = (selectedCompromissos: CompromissoWithPaymentStatus[]): void => {
  const validCompromissos = selectedCompromissos.filter((c) => {
    return Number(c.compromisso_status_financeiro) !== 2 && (c.valor_restante || 0) > 0;
  });

  if (validCompromissos.length === 0) {
    eventBus.emit('globalMessage.warn', t('Nenhum compromisso selecionado possui valor pendente'));
    return;
  }

  const totalValor = validCompromissos.reduce((sum, c) => sum + (c.valor_restante || 0), 0);

  confirmAction({
    message: `${t('Serão criados')} ${validCompromissos.length} ${t('recebimentos totalizando')} ${n(parseFloat(String(totalValor)) || 0, 'currency')}. ${t('Deseja continuar?')}`,
    header: t('Confirmar Criação em Lote'),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('Criar Recebimentos'),
    rejectLabel: t('Cancelar'),
    onAccept: () => executeBulkRecebimentos(validCompromissos),
    onReject: () => {
      log('RevenueConciliationWizard', 'handleBulkCreateRecebimentos', 'User cancelled bulk creation');
    }
  });
};

const executeBulkRecebimentos = async (compromissosToCreate: CompromissoWithPaymentStatus[]): Promise<void> => {
  log('RevenueConciliationWizard', 'executeBulkRecebimentos', 'Starting bulk creation', { count: compromissosToCreate.length });
  isBulkCreating.value = true;

  try {
    const results = (await createBulkRecebimentos(compromissosToCreate)) as BulkRecebimentosResults;

    log('RevenueConciliationWizard', 'executeBulkRecebimentos', 'Bulk creation results', results);

    if (results.success.length > 0 && results.errors.length === 0) {
      eventBus.emit('globalMessage.success', `${results.success.length} ${t('recebimentos criados com sucesso')}`);
    } else if (results.success.length > 0 && results.errors.length > 0) {
      eventBus.emit('globalMessage.warn', `${results.success.length} ${t('recebimentos criados')}, ${results.errors.length} ${t('erros')}`);
    } else if (results.errors.length > 0) {
      eventBus.emit('globalMessage.error', `${t('Erro ao criar recebimentos')}: ${results.errors.length} ${t('falhas')}`);
    }

    // Clear selection and reload data
    if (paymentStatusTableRef.value) {
      paymentStatusTableRef.value.clearSelection();
    }
    await loadPaymentStatusData();

    eventBus.emit('recebimento.bulkCreated', results);
  } catch (error: unknown) {
    log('RevenueConciliationWizard', 'executeBulkRecebimentos', 'Critical error', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao criar recebimentos em lote') + ': ' + message);
  } finally {
    isBulkCreating.value = false;
  }
};

// Methods
const loadConciliationSummary = async (): Promise<void> => {
  log('RevenueConciliationWizard', 'loadConciliationSummary', 'Loading summary', {
    periodId: props.periodId
  });

  try {
    const summary = (await getRevenueConciliationSummary(props.periodId)) as RevenueConciliationSummary;
    conciliationStatus.value = summary.status;
    conciliationAmount.value = summary.amount;

    log('RevenueConciliationWizard', 'loadConciliationSummary', 'Summary loaded', summary);
  } catch (error: unknown) {
    log('RevenueConciliationWizard', 'loadConciliationSummary', 'Error', error);
    eventBus.emit('globalMessage.error', t('Error loading conciliation summary'));
  }
};

// Check if franchise has a subaccount configured
const checkFranchiseSubaccount = async (): Promise<void> => {
  if (!baasEnabled) { franchiseHasSubaccount.value = false; return; } // degrade: no link column, no call
  log('RevenueConciliationWizard', 'checkFranchiseSubaccount', 'Checking subaccount for franchise', props.franchiseId);
  try {
    const subaccount = await getSubaccountByFranchise(props.franchiseId);
    franchiseHasSubaccount.value = subaccount !== null && subaccount !== undefined;
    log('RevenueConciliationWizard', 'checkFranchiseSubaccount', 'Subaccount status', {
      hasSubaccount: franchiseHasSubaccount.value,
      subaccount
    });
  } catch (err: unknown) {
    log('RevenueConciliationWizard', 'checkFranchiseSubaccount', 'Error checking subaccount', err);
    franchiseHasSubaccount.value = false;
  }
};

const loadPaymentStatusData = async (): Promise<void> => {
  log('RevenueConciliationWizard', 'loadPaymentStatusData', 'Loading payment status data', {
    franchiseId: props.franchiseId,
    startDate: props.startDate,
    endDate: props.endDate,
    startDateType: typeof props.startDate,
    endDateType: typeof props.endDate
  });

  isLoadingPaymentStatus.value = true;

  try {
    let data: CompromissoWithPaymentStatus[];

    // Use queryOverride if dates are available, otherwise use session filter
    if (props.startDate && props.endDate) {
      log('RevenueConciliationWizard', 'loadPaymentStatusData', '✅ Using queryOverride with dates', {
        startDate: props.startDate,
        endDate: props.endDate,
        formattedStartDate: formatDateForApi(props.startDate),
        formattedEndDate: formatDateForApi(props.endDate)
      });
      const queryOverride = {
        franquiaIds: [props.franchiseId],
        dataInicio: formatDateForApi(props.startDate),
        dataFim: formatDateForApi(props.endDate)
      };
      data = (await fetchCompromissosWithPaymentStatus({}, { queryOverride })) as CompromissoWithPaymentStatus[];
    } else {
      log('RevenueConciliationWizard', 'loadPaymentStatusData', '❌ Dates not available, using session filter', {
        startDate: props.startDate,
        endDate: props.endDate
      });
      // Use session filter (user's current franchise context) when dates aren't available
      data = (await fetchCompromissosWithPaymentStatus({}, {})) as CompromissoWithPaymentStatus[];
    }

    compromissosWithPaymentStatus.value = data;
    paymentMetrics.value = calculateDashboardMetrics(data) as PaymentMetrics;

    log('RevenueConciliationWizard', 'loadPaymentStatusData', 'Loaded data', {
      count: data.length,
      metrics: paymentMetrics.value
    });
  } catch (error: unknown) {
    log('RevenueConciliationWizard', 'loadPaymentStatusData', 'Error', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar status de pagamento') + ': ' + message);
    compromissosWithPaymentStatus.value = [];
    paymentMetrics.value = {
      total: 0,
      totalValor: 0,
      complete: 0,
      completeValor: 0,
      partial: 0,
      partialValor: 0,
      due: 0,
      dueValor: 0
    };
  } finally {
    isLoadingPaymentStatus.value = false;
  }
};

/**
 * Load available recebimentos for the wizard
 *
 * ╔════════════════════════════════════════════════════════════════════════════╗
 * ║                    HYBRID ARCHITECTURE IMPLEMENTATION                       ║
 * ╠════════════════════════════════════════════════════════════════════════════╣
 * ║                                                                            ║
 * ║  Uses fetchAvailableRecebimentosForPaymentRules()                          ║
 * ║  → Filters out recebimentos already claimed by ANY of these payment rules  ║
 * ║  → Business rule: Same rule can't claim same recebimento twice             ║
 * ║                                                                            ║
 * ╚════════════════════════════════════════════════════════════════════════════╝
 */
const loadAvailableRecebimentos = async (): Promise<void> => {
  log('RevenueConciliationWizard', 'loadAvailableRecebimentos', 'Loading available recebimentos', {
    franchiseId: props.franchiseId,
    startDate: props.startDate,
    endDate: props.endDate,
    userIds: props.userIds,
    paymentRuleIds: props.paymentRuleIds
  });

  isLoadingRecebimentos.value = true;

  try {
    // Filter by payment rule IDs - returns recebimentos NOT yet claimed by any of these rules
    // If dates aren't available, fetch without date filtering (uses session filter)
    const data = (await fetchAvailableRecebimentosForPaymentRules(
      props.franchiseId,
      props.startDate,  // May be null - service should handle this
      props.endDate,    // May be null - service should handle this
      props.paymentRuleIds as unknown as never[],
      props.userIds as unknown as never[]
    )) as Recebimento[];

    if (data.length > 0) {
      log('RevenueConciliationWizard', 'loadAvailableRecebimentos', 'First available recebimento structure', data[0]);
    }

    availableRecebimentos.value = data;

    log('RevenueConciliationWizard', 'loadAvailableRecebimentos', 'Loaded', {
      count: data.length
    });
  } catch (error: unknown) {
    log('RevenueConciliationWizard', 'loadAvailableRecebimentos', 'Error', error);
    eventBus.emit('globalMessage.error', t('Error loading available recebimentos'));
    availableRecebimentos.value = [];
  } finally {
    isLoadingRecebimentos.value = false;
  }
};

const loadLinkedRecebimentos = async (): Promise<void> => {
  log('RevenueConciliationWizard', 'loadLinkedRecebimentos', 'Loading linked recebimentos');

  try {
    const linkedRecebimentos = (await getLinkedRecebimentosAPI(props.periodId)) as Recebimento[];

    originallyLinkedRecebimentos.value = [...linkedRecebimentos];
    selectedRecebimentos.value = [...linkedRecebimentos];

    log('RevenueConciliationWizard', 'loadLinkedRecebimentos', 'Loaded', {
      count: linkedRecebimentos.length
    });
  } catch (error: unknown) {
    log('RevenueConciliationWizard', 'loadLinkedRecebimentos', 'Error', error);
    originallyLinkedRecebimentos.value = [];
    selectedRecebimentos.value = [];
  }
};

// Handle step change - load data for the next step
// Event payload is { from, to } from useWizard composable
const handleStepChange = async (event: { from: number; to: number }): Promise<void> => {
  const { from, to } = event;
  log('RevenueConciliationWizard', 'handleStepChange', 'Step changed', { from, to });
  currentStepIndex.value = to;

  // When entering Step 1, ALWAYS reload available and linked recebimentos
  // This ensures new recebimentos from Step 0 appear, and handles back>next navigation
  if (to === 1) {
    log('RevenueConciliationWizard', 'handleStepChange', 'Entering Step 1 - reloading recebimentos');
    await Promise.all([
      loadAvailableRecebimentos(),
      loadLinkedRecebimentos()
    ]);
  }
};

const completeRevenueConciliation = async (): Promise<void> => {
  if (selectedRecebimentos.value.length === 0) {
    eventBus.emit('globalMessage.warning', t('Please select at least one recebimento before completing'));
    return;
  }

  log('RevenueConciliationWizard', 'completeRevenueConciliation', 'Starting conciliation process', {
    selectedCount: selectedRecebimentos.value.length,
    originalCount: originallyLinkedRecebimentos.value.length
  });

  isCompleting.value = true;

  try {
    const originalIds: Id[] = originallyLinkedRecebimentos.value.map((r) => r.recebimento_id);
    const selectedIds: Id[] = selectedRecebimentos.value.map((r) => r.recebimento_id);

    const toUnlink = originalIds.filter((id) => !selectedIds.includes(id));
    const toLink = selectedIds.filter((id) => !originalIds.includes(id));

    log('RevenueConciliationWizard', 'completeRevenueConciliation', 'Diff calculated', {
      toUnlink: toUnlink.length,
      toLink: toLink.length
    });

    if (toUnlink.length > 0) {
      log('RevenueConciliationWizard', 'completeRevenueConciliation', `Unlinking ${toUnlink.length} recebimentos`);
      await Promise.all(
        toUnlink.map((recebimentoId) => unlinkRecebimentoFromPeriod(props.periodId, recebimentoId))
      );
      log('RevenueConciliationWizard', 'completeRevenueConciliation', 'Unlinks completed');
    }

    if (toLink.length > 0) {
      log('RevenueConciliationWizard', 'completeRevenueConciliation', `Linking ${toLink.length} recebimentos`);
      await linkRecebimentosToPeriod(props.periodId, toLink as unknown as never[]);
      log('RevenueConciliationWizard', 'completeRevenueConciliation', 'Links completed');
    }

    const result = await completeRevenueConciliationAPI(props.periodId);

    log('RevenueConciliationWizard', 'completeRevenueConciliation', 'Conciliation completed', result);

    emit('completed', result);
    eventBus.emit('revenue-conciliation.completed', { periodId: props.periodId, result });
    onClose();
  } catch (error: unknown) {
    log('RevenueConciliationWizard', 'completeRevenueConciliation', 'Error', error);
    eventBus.emit('globalMessage.error', t('Erro ao finalizar conciliação de recebimentos'));
  } finally {
    isCompleting.value = false;
  }
};

const onClose = (): void => {
  log('RevenueConciliationWizard', 'onClose', 'Closing wizard');
  emit('update:visible', false);
  emit('closed');
};

// Track if data has been loaded (to prevent duplicate loads)
const hasLoadedData = ref<boolean>(false);

// Watch for visibility changes
watch(() => props.visible, async (newVal) => {
  isVisible.value = newVal;
  if (newVal) {
    log('RevenueConciliationWizard', 'watch:visible', 'Wizard opening, checking dates', {
      startDate: props.startDate,
      endDate: props.endDate
    });

    // Reset wizard state when opening
    currentStepIndex.value = 0;
    originallyLinkedRecebimentos.value = [];
    selectedRecebimentos.value = [];
    availableRecebimentos.value = [];
    compromissosWithPaymentStatus.value = [];
    paymentMetrics.value = null;
    franchiseHasSubaccount.value = false;
    hasLoadedData.value = false;

    // Load data on wizard open - works even without dates (uses session filter)
    await Promise.all([
      loadConciliationSummary(),
      loadPaymentStatusData(),
      checkFranchiseSubaccount()
    ]);
    hasLoadedData.value = true;
    log('RevenueConciliationWizard', 'watch:visible', 'Wizard opened - data loaded');
  }
});

// Watch for dates to become available (reload with date filtering when dates arrive)
// When creating a new payroll, dates might be null initially and become available later
watch(() => [props.startDate, props.endDate], async ([newStartDate, newEndDate], [oldStartDate, oldEndDate]) => {
  // Reload data with date filtering when dates become available
  const datesNowAvailable = newStartDate && newEndDate;
  const datesWereNull = !oldStartDate && !oldEndDate;

  log('RevenueConciliationWizard', 'watch:dates', 'Dates changed', {
    oldStartDate,
    oldEndDate,
    newStartDate,
    newEndDate,
    datesNowAvailable,
    isVisible: isVisible.value
  });

  // Reload with date filtering when dates become available
  if (isVisible.value && datesWereNull && datesNowAvailable) {
    log('RevenueConciliationWizard', 'watch:dates', 'Dates became available, reloading with date filtering', {
      startDate: newStartDate,
      endDate: newEndDate
    });
    await Promise.all([
      loadPaymentStatusData(),
      checkFranchiseSubaccount()
    ]);
  }
});

// Listen for recebimento events to refresh data
const handleRecebimentoCreated = (): void => {
  log('RevenueConciliationWizard', 'handleRecebimentoCreated', 'Recebimento created, refreshing data');
  loadPaymentStatusData();
  loadAvailableRecebimentos();
};

// Event listeners setup (we'd normally do this in onMounted but wizard manages its own lifecycle)
</script>

<style scoped>
/* Step Content Panels */
.step-panel {
  width: 100%;
}

.step-panel h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.step-description {
  margin-bottom: 1.5rem;
  color: var(--text-color-secondary);
}

/* Selection Summary */
.selection-summary {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  padding: 1rem;
  margin-bottom: 1.5rem;
  background: var(--surface-50);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
}

.summary-item {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
}

.summary-label {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  font-weight: 500;
}

.summary-value {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--text-color);
}

.summary-divider {
  width: 1px;
  height: 30px;
  background: var(--surface-border);
}

/* Recebimentos DataTable */
.recebimentos-datatable {
  margin-top: 1rem;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: var(--text-color-secondary);
}

.loading-state i {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: var(--primary-color);
}

.loading-state p {
  margin: 0;
  font-size: 1rem;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: var(--text-color-secondary);
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-state p {
  margin: 0;
  font-size: 1rem;
}

.empty-state-hint {
  margin-top: 0.5rem !important;
  font-size: 0.875rem !important;
  font-style: italic;
}


/* Final Summary */
.final-summary {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 1rem 0;
}

.summary-section h4 {
  margin: 0 0 1rem 0;
  font-size: 1.125rem;
  font-weight: 600;
}

.summary-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  background: var(--surface-50);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--surface-border);
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-row.highlight {
  background: var(--primary-50);
  padding: 1rem;
  margin: 0 -1rem;
  border-radius: 6px;
  border: 1px solid var(--primary-200);
}

.summary-row .label {
  font-weight: 500;
  color: var(--text-color-secondary);
}

.summary-row .value {
  font-weight: 600;
  font-size: 1.125rem;
  color: var(--text-color);
}

.summary-row.highlight .value {
  font-size: 1.5rem;
  color: var(--primary-color);
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  text-transform: uppercase;
  font-weight: 600;
}

.status-badge.pending {
  background: var(--yellow-100);
  color: var(--yellow-900);
}

.status-badge.in_progress {
  background: var(--blue-100);
  color: var(--blue-900);
}

.status-badge.completed {
  background: var(--green-100);
  color: var(--green-900);
}

/* Completion Message */
.completion-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  background: var(--green-50);
  border: 2px solid var(--green-200);
  border-radius: 8px;
  text-align: center;
}

.completion-message i {
  font-size: 3rem;
  color: var(--green-500);
}

.completion-message p {
  margin: 0;
  color: var(--text-color);
  font-size: 1rem;
  line-height: 1.5;
}

.completion-message .note {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  font-style: italic;
}

/* Responsive */
@media (max-width: 768px) {
  .selection-summary {
    flex-direction: column;
    gap: 0.75rem;
  }

  .summary-divider {
    width: 100%;
    height: 1px;
  }
}
</style>
